import axios, { type AxiosError } from "axios";
import { API_URLS } from "@/config/urls";
import { api } from "@/lib/axios";
import type {
  CalendarEntryFormTimed,
  EventApiResponse,
  EventCreateApiRequest,
  PaginatedResponse,
  ReminderData,
} from "@/lib/humhub-api/types";

function isAxiosError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error);
}

export const eventsApi = {
  getAllEvents: async (
    page = 1,
    perPage = 20,
    startDate = new Date().toISOString().split("T")[0],
    additionalParams = {},
  ): Promise<PaginatedResponse<EventApiResponse>> => {
    try {
      const params = {
        page,
        limit: perPage,
        "start-date": startDate,
        ...additionalParams,
      };

      const response = await api.get<PaginatedResponse<EventApiResponse>>(API_URLS.CALENDAR, {
        params,
      });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  createEvent: async (
    containerId: number,
    event: EventCreateApiRequest,
    files: File[],
  ): Promise<EventApiResponse> => {
    try {
      // TODO(proposal): createEvent の引数をフラットDTO（例: ClientEventCreateRequest）にし、
      //  ここでAPI形（EventCreateApiRequest）および FormData への変換を完結させると
      //  呼び出し側の型推論が容易になり、all_day/時刻の分岐も境界層に閉じ込められる。
      const formData = new FormData();

      // Add event data
      formData.append("CalendarEntry[title]", event.CalendarEntry.title);
      formData.append("CalendarEntry[description]", event.CalendarEntry.description);
      formData.append("CalendarEntry[color]", event.CalendarEntry.color);
      formData.append("CalendarEntry[all_day]", event.CalendarEntry.all_day.toString());
      formData.append(
        "CalendarEntry[participation_mode]",
        event.CalendarEntry.participation_mode.toString(),
      );
      formData.append(
        "CalendarEntry[max_participants]",
        event.CalendarEntry.max_participants.toString(),
      );
      formData.append("CalendarEntry[allow_decline]", event.CalendarEntry.allow_decline.toString());
      formData.append("CalendarEntry[allow_maybe]", event.CalendarEntry.allow_maybe.toString());
      formData.append("CalendarEntry[participant_info]", event.CalendarEntry.participant_info);

      // Add form data
      formData.append("CalendarEntryForm[start_date]", event.CalendarEntryForm.start_date);
      formData.append("CalendarEntryForm[end_date]", event.CalendarEntryForm.end_date);

      // 時刻フィールドは all_day=0（時刻指定）のときのみ送信
      if (event.CalendarEntry.all_day === 0) {
        const timedEntryForm = event.CalendarEntryForm as CalendarEntryFormTimed;
        formData.append("CalendarEntryForm[start_time]", timedEntryForm.start_time);
        formData.append("CalendarEntryForm[end_time]", timedEntryForm.end_time);
      }
      formData.append("CalendarEntryForm[timeZone]", event.CalendarEntryForm.timeZone);
      formData.append("CalendarEntryForm[location]", event.CalendarEntryForm.location);
      formData.append("CalendarEntryForm[language]", event.CalendarEntryForm.language);
      formData.append("CalendarEntryForm[is_draft]", event.CalendarEntryForm.is_draft.toString());
      formData.append(
        "CalendarEntryForm[online_location]",
        event.CalendarEntryForm.online_location,
      );
      formData.append(
        "CalendarEntryForm[location_type]",
        String(event.CalendarEntryForm.location_type),
      );
      formData.append("CalendarEntryForm[forceJoin]", event.CalendarEntryForm.forceJoin.toString());
      formData.append("CalendarEntryForm[topics]", event.CalendarEntryForm.topics);
      formData.append("CalendarEntryForm[is_public]", event.CalendarEntryForm.is_public.toString());
      formData.append("CalendarEntryForm[hidden]", event.CalendarEntryForm.hidden.toString());

      // Add reminders
      event.CalendarEntryForm.reminders.forEach((reminder, index) => {
        formData.append(`CalendarEntryForm[reminders][${index}][type]`, reminder.type.toString());
        formData.append(`CalendarEntryForm[reminders][${index}][unit]`, reminder.unit.toString());
        formData.append(`CalendarEntryForm[reminders][${index}][value]`, reminder.value.toString());
      });

      // Add files
      files.forEach((file) => {
        formData.append("files[]", file);
      });

      const response = await api.post<EventApiResponse>(
        `${API_URLS.CALENDAR}/container/${containerId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getGroupEvents: async (
    page = 1,
    perPage = 20,
    short = true,
    containerId?: number,
    startDate?: string,
    endDate?: string,
  ): Promise<PaginatedResponse<EventApiResponse>> => {
    try {
      const params: Record<string, string | number | boolean | undefined> = {
        page,
        limit: perPage,
        short,
        "container-id": containerId,
        "group-member-only": true,
      };

      if (startDate) {
        params["start-date"] = startDate;
      }
      if (endDate) {
        params["end-date"] = endDate;
      }

      const response = await api.get<PaginatedResponse<EventApiResponse>>(API_URLS.CALENDAR, {
        params,
      });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getEventById: async (id: number): Promise<EventApiResponse> => {
    try {
      const response = await api.get<EventApiResponse>(`${API_URLS.CALENDAR}/entry/${id}`);
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getPublicEventByCode: async (code: string): Promise<EventApiResponse> => {
    try {
      const response = await api.get<EventApiResponse>(
        `${API_URLS.CALENDAR}/entry/${code}/public-view`,
      );

      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  respondToEvent: async (id: number, respondType: number): Promise<void> => {
    try {
      await api.post(`${API_URLS.CALENDAR}/entry/${id}/respond`, { type: respondType });
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  createEventReminders: async (eventId: number, reminders: ReminderData[]): Promise<void> => {
    try {
      await api.post(`${API_URLS.CALENDAR}/entry/${eventId}/reminder`, { reminders });
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },

  deleteAllEventReminders: async (eventId: number): Promise<void> => {
    try {
      await api.delete(`${API_URLS.CALENDAR}/entry/${eventId}/user-all-reminders`);
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  updateEvent: async (id: number, event: EventCreateApiRequest): Promise<EventApiResponse> => {
    try {
      const response = await api.put<EventApiResponse>(`${API_URLS.CALENDAR}/entry/${id}`, event);
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  deleteEvent: async (id: number): Promise<void> => {
    try {
      await api.delete(`${API_URLS.CALENDAR}/entry/${id}`);
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  replaceEventFile: async (id: number, file: File): Promise<void> => {
    try {
      const formData = new FormData();
      formData.append("files[]", file);

      await api.post(`${API_URLS.CALENDAR}/entry/${id}/replace-file`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getMyEvents: async (
    page = 1,
    perPage = 20,
    short = true,
    startDate?: string,
    endDate?: string,
    userParticipation?: string,
    additionalParams?: Record<string, string | number | boolean | undefined>,
  ): Promise<PaginatedResponse<EventApiResponse>> => {
    try {
      const params: Record<string, string | number | boolean | undefined> = {
        page,
        limit: perPage,
        short,
        ...additionalParams,
      };

      if (startDate) {
        params["start-date"] = startDate;
      }
      if (endDate) {
        params["end-date"] = endDate;
      }
      if (userParticipation) {
        params["user-participation"] = userParticipation;
      }

      const response = await api.get<PaginatedResponse<EventApiResponse>>(API_URLS.CALENDAR, {
        params,
      });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getRegisteredEvents: async (
    page = 1,
    perPage = 20,
    short = true,
    startDate?: string,
    endDate?: string,
    userParticipation?: string,
  ): Promise<PaginatedResponse<EventApiResponse>> => {
    try {
      const params: Record<string, string | number | boolean | undefined> = {
        page,
        limit: perPage,
        short,
        "user-participation": userParticipation,
      };

      if (startDate) {
        params["start-date"] = startDate;
      }
      if (endDate) {
        params["end-date"] = endDate;
      }

      const response = await api.get<PaginatedResponse<EventApiResponse>>(API_URLS.CALENDAR, {
        params,
      });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  updateAllParticipantsReminders: async (
    eventId: number,
    reminders: ReminderData[],
  ): Promise<void> => {
    try {
      await api.post(`${API_URLS.CALENDAR}/entry/${eventId}/all-participants-reminder`, {
        reminders,
      });
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  deleteAllParticipantsReminders: async (eventId: number): Promise<void> => {
    try {
      await api.delete(`${API_URLS.CALENDAR}/entry/${eventId}/all-participants-reminder`);
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
};
